import { TodaySummary } from "./SummaryCards";

/**
 * Occurrence shape the Today page hands us (subset — only the fields
 * the summary derivation reads). Kept local to keep this module free
 * of a dependency on the full OccurrenceRow type in index.tsx.
 */
export type SummarySource = {
  id: number;
  scheduled_date: string;
  teaching_mode: "Online" | "Offline" | "Hybrid" | null;
  location_id: number | null;
  location_name: string | null;
  is_cancelled: boolean;
  is_makeup: boolean;
  available_spots: number;
  students: Array<{
    id: number;
    has_outstanding_invoice: boolean;
  }>;
};

/**
 * Derive the Today KPI summary from the *filtered* occurrence set the
 * schedule matrix renders. Guarantees the invariant "KPIs describe
 * exactly what the user is looking at" — server-side filters
 * (location/mode/staff_id) narrow the payload, client-side filters
 * (search/spots/makeup/outstanding) narrow the array, and this
 * function derives the KPI strip from the final result so the two
 * cannot drift.
 *
 * Semantics match the previous server-derived summary exactly (see
 * TimetableController::week): total_classes includes cancelled;
 * online/in_person/hybrid/makeup/spots exclude cancelled; hybrid
 * rolls into BOTH online and in_person totals; unique student counts
 * exclude cancelled.
 */
export function deriveTodaySummary(
  occurrences: SummarySource[],
  selectedIso: string,
): TodaySummary {
  const alive = occurrences.filter((o) => !o.is_cancelled);

  const unique = (rows: SummarySource[]) => {
    const s = new Set<number>();
    for (const r of rows) for (const st of r.students) s.add(st.id);
    return s.size;
  };

  // In-person by location breakdown — mirrors backend structure.
  const byLocationMap = new Map<
    string,
    { location_id: number | string; location_name: string | null; class_count: number; student_set: Set<number> }
  >();
  for (const o of alive) {
    if (o.teaching_mode !== "Offline") continue;
    const key = String(o.location_id ?? "unknown");
    const entry = byLocationMap.get(key) ?? {
      location_id: o.location_id ?? key,
      location_name: o.location_name,
      class_count: 0,
      student_set: new Set<number>(),
    };
    entry.class_count += 1;
    for (const st of o.students) entry.student_set.add(st.id);
    byLocationMap.set(key, entry);
  }

  const outstandingStudentIds = new Set<number>();
  for (const o of alive) {
    for (const st of o.students) if (st.has_outstanding_invoice) outstandingStudentIds.add(st.id);
  }

  return {
    date: selectedIso,
    total_classes: occurrences.length,
    total_unique_students: unique(alive),
    online_classes: alive.filter((o) => o.teaching_mode === "Online" || o.teaching_mode === "Hybrid").length,
    in_person_classes: alive.filter((o) => o.teaching_mode === "Offline" || o.teaching_mode === "Hybrid").length,
    hybrid_classes: alive.filter((o) => o.teaching_mode === "Hybrid").length,
    makeup_classes: alive.filter((o) => o.is_makeup).length,
    cancelled_classes: occurrences.filter((o) => o.is_cancelled).length,
    available_spots: alive.reduce((a, o) => a + (o.available_spots || 0), 0),
    students_with_outstanding: outstandingStudentIds.size,
    by_location: Array.from(byLocationMap.values()).map((e) => ({
      location_id: e.location_id,
      location_name: e.location_name,
      class_count: e.class_count,
      student_count: e.student_set.size,
    })),
  };
}
